"use client"

import { useState } from "react"
import { ChevronRight, Link2, Check, Scale, CreditCard } from "lucide-react"
import Navbar from "@/app/Components/NavbarSection"
import Footer from "@/app/Sections/Footer"

const sections = [
    {
        id: "information-we-collect",
        number: "01",
        title: "Information We Collect",
        body: "To provide you with reliable hosting services, we collect personal information (name, email, billing data), usage data (IP address, browser type, access times), and technical data when you interact with our platform. As a provider, we also store Customer Content (files, databases, emails) solely to provide the services or comply with legal requirements. We use cookies to analyze traffic and improve functionality.",
    },
    {
        id: "how-we-use",
        number: "02",
        title: "How We Use Your Information",
        body: "We use collected information to manage your account, process secure payments, maintain our infrastructure, and provide technical support. We also use it to send administrative notices (service updates, maintenance alerts) and to detect or prevent fraud and security threats.",
    },
    {
        id: "how-we-share",
        number: "03",
        title: "How We Share Your Information",
        body: "Aurex Hosting does not sell or rent your personal information. We only share data with trusted service providers (payment gateways, helpdesk software) necessary for business operations, for legal compliance (subpoenas, court orders), or to protect our rights and infrastructure safety.",
    },
    {
        id: "data-security",
        number: "04",
        title: "Data Security",
        body: "We implement industry-standard security measures, including encryption, firewalls, and SSL technology, to protect your data from unauthorized access. However, no internet transmission or electronic storage is 100% secure, so we cannot guarantee absolute security.",
    },
    {
        id: "your-rights",
        number: "05",
        title: "Your Rights and Choices",
        body: "You can access and update your personal/billing info through the client portal. You may also request account and data deletion, though certain records may be retained for legal or tax purposes. You can opt out of promotional emails while still receiving essential service notices.",
    },
    {
        id: "childrens-privacy",
        number: "06",
        title: "Children's Privacy",
        body: "Our services are strictly limited to individuals 13 years of age or older. We do not knowingly collect data from children under 13. If we discover inadvertent collection of such data, we will delete it immediately.",
    },
    {
        id: "third-party-links",
        number: "07",
        title: "Third-Party Links",
        body: "Our services may contain links to external sites. We are not responsible for the privacy practices or content of these third-party websites and encourage you to read their respective privacy policies.",
    },
    {
        id: "changes",
        number: "08",
        title: "Changes to This Privacy Policy",
        body: "We may update this policy to reflect changes in our practices or legal obligations. Significant changes will be announced on our website with an updated 'Last Updated' date.",
    },
    {
        id: "contact-us",
        number: "09",
        title: "Contact Us",
        body: "If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please contact our support team or reach out to us at our official legal address.",
    },
    {
        id: "refund-policy",
        number: "10",
        title: "Refund Policy",
        body: "For detailed information regarding service cancellations and refunds, please refer to our full Refund Policy page.",
    },
]

function CopyLinkButton({ id }: { id: string }) {
    const [copied, setCopied] = useState(false)
    const copy = () => {
        if (typeof window === "undefined") return
        navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${id}`)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <button
            onClick={copy}
            aria-label="Copy section link"
            className="flex-shrink-0 p-1.5 rounded-md text-gray-600 hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Link2 className="w-3.5 h-3.5" />}
        </button>
    )
}

export default function PrivacyPolicyPage() {
    return (
        <div className="bg-black">
            <Navbar />
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "url('https://cdn.aurex.lk/assets/hero-mc.webp')",
                            backgroundSize: "cover",
                            backgroundPosition: "center top",
                            filter: "blur(6px) brightness(0.45)",
                            transform: "scale(1.05)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#3b0d6e]/40" />
                    <div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                    />
                    <div
                        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
                        style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-24">
                        <nav className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 mb-8">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-gray-400">Legal</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-white">Privacy Policy</span>
                        </nav>
                        <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight tracking-wide mb-3">
                            Privacy Policy
                        </h1>
                        <p className="text-gray-500 font-poppins text-sm">Last updated: May 7, 2026</p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
                        <svg width="100%" height="50" viewBox="0 0 2598 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet" style={{ display: "block", position: "relative", zIndex: 20 }}>
                            <path d="M2598 150H0L230.359 0H2598V150Z" fill="black" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="relative z-10 w-full bg-black">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 relative">

                    <div className="flex flex-wrap gap-3 mb-10">
                        <a href="/tos" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Scale className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Terms of Service</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                        <a href="/refund-policy" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <CreditCard className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Refund Policy</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                    </div>

                    <p className="text-gray-400 font-poppins text-sm leading-relaxed mb-10 border-l-4 border-[#8B5CF6] pl-4">
                        At Aurex Hosting, your privacy matters. This policy explains what data we collect, how we use it, and the choices you have. We are committed to handling your information responsibly and transparently.
                    </p>

                    <div className="flex flex-col">
                        {sections.map((s, i) => (
                            <div key={s.id}>
                                <section id={s.id} className="py-8">
                                    <div className="flex items-start gap-3 mb-4">
                                        <span className="font-akira text-[#8B5CF6]/50 text-xs mt-1 flex-shrink-0 select-none">{s.number}</span>
                                        <h2 className="font-akira text-white text-base sm:text-lg leading-snug flex-1">{s.title}</h2>
                                        <CopyLinkButton id={s.id} />
                                    </div>
                                    <p className="text-gray-400 font-poppins text-sm leading-relaxed pl-7">{s.body}</p>
                                </section>
                                {i < sections.length - 1 && (
                                    <hr className="border-white/5" />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 pt-8 border-t border-white/5">
                        <p className="text-gray-600 font-poppins text-xs">
                            Questions about your data?{" "}
                            <a href="mailto:privacy@aurex.lk" className="text-[#8B5CF6] hover:text-purple-400 transition-colors">
                                privacy@aurex.lk
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
